import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Switch,
  Tag,
  Space,
  Tooltip,
  Typography,
  Dropdown,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SyncOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  MailOutlined,
  KeyOutlined,
  SafetyOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import PageHeader from '../../components/PageHeader';
import ResponsiveTableOrCards from '../../components/ResponsiveTableOrCards';
import UserListCard from '../../components/UserListCard';
import { ALLOWED_CHILD_ROLES } from '../../components/UserForm';
import { useAuth } from '../../auth/AuthContext';
import type { UserListCardHandlers } from '../../components/UserListCard';
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  reactivateUser,
  resendUserInvite,
  sendUserPasswordReset,
  resetUserMfa,
  permanentDeleteUser,
  syncCognitoUsers,
  getOrganizations,
  User,
  Organization,
} from '../../services/adminService';
import PageHelp from '../../components/PageHelp';
import UserForm, { UserFormValues } from '../../components/UserForm';
import ResponsiveModal from '../../components/ResponsiveModal';

const { Text } = Typography;

const UserList: React.FC = () => {
  const auth = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchUsers = async (opts?: { includeInactive?: boolean }) => {
    setLoading(true);
    try {
      const data = await getUsers({ includeInactive: opts?.includeInactive ?? showInactive });
      setUsers(data);
    } catch (err: any) {
      message.error(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async (record: User) => {
    try {
      await reactivateUser(record.id);
      message.success(`Reactivated ${record.first_name} ${record.last_name}`);
      fetchUsers();
    } catch (err: any) {
      message.error(err.message || 'Failed to reactivate user');
    }
  };

  const handleResendInvite = async (record: User) => {
    try {
      await resendUserInvite(record.id);
      message.success(`New activation link sent to ${record.email}`);
    } catch (err: any) {
      message.error(err.message || 'Failed to resend invitation');
    }
  };

  const handlePasswordReset = async (record: User) => {
    try {
      await sendUserPasswordReset(record.id);
      message.success(`Password reset link sent to ${record.email}`);
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : 'Failed to send the password reset');
    }
  };

  // KAN-207: only for users who have activated (not yet activated → Resend
  // invite), never yourself, and only roles you could create. The backend
  // enforces the same rules; this just hides an option that would fail.
  const canSendPasswordReset = (record: User) =>
    !record.invite_pending &&
    !!record.cognito_username &&
    record.email !== auth.user?.email &&
    (ALLOWED_CHILD_ROLES[auth.role || ''] || []).includes(record.role);

  const handleResetMfa = async (record: User) => {
    try {
      await resetUserMfa(record.id);
      message.success(`MFA reset for ${record.email}`);
    } catch (err: any) {
      message.error(err.message || 'Failed to reset MFA');
    }
  };

  const handlePermanentDelete = (record: User) => {
    Modal.confirm({
      title: 'Permanently delete user?',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p>
            <strong>
              {record.first_name} {record.last_name}
            </strong>{' '}
            ({record.email}) will be removed from Cognito and can never log in again. The platform
            users row is preserved for audit-log integrity, but the cognito_sub link is cleared so
            this is irreversible.
          </p>
          <p style={{ color: '#ff4d4f' }}>
            Use 'Reactivate' if you might want this user back. Only choose this when you're
            absolutely sure.
          </p>
        </div>
      ),
      okText: 'Permanently delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await permanentDeleteUser(record.id);
          message.success(`Permanently deleted ${record.email}`);
          fetchUsers();
        } catch (err: any) {
          message.error(err.message || 'Failed to permanently delete user');
        }
      },
    });
  };

  const fetchOrganizations = async () => {
    try {
      const data = await getOrganizations();
      setOrganizations(data);
    } catch {
      // Silently fail
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchUsers({ includeInactive: showInactive });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const handleSync = async () => {
    try {
      setLoading(true);
      const result = await syncCognitoUsers();
      message.success(`Synced: ${result.imported} new user(s) imported from Cognito`);
      fetchUsers();
    } catch (err: any) {
      message.error(err.message || 'Sync failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values: any) => {
    setSubmitting(true);
    try {
      const created = await createUser({
        email: values.email!,
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.role,
        organizationId: values.organizationId,
        department: values.department || undefined,
        canViewSubordinateSims: values.canViewSubordinateSims,
        canViewYachtSims: values.canViewYachtSims,
      });
      if (created.invite === 'email_failed') {
        message.warning(
          `User created, but the activation email to ${values.email} could not be sent. Use "Resend invite" to try again.`,
          8
        );
      } else {
        message.success(`Activation link sent to ${values.email}`);
      }
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchUsers();
    } catch (err: any) {
      message.error(err.message || 'Failed to invite user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record: User) => {
    setEditingUser(record);
    setEditModalVisible(true);
  };

  const handleUpdate = async (values: UserFormValues) => {
    if (!editingUser) return;
    setSubmitting(true);
    try {
      await updateUser(editingUser.id, {
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.role,
        department: values.department || undefined,
        organizationId: values.organizationId,
        canViewSubordinateSims: values.canViewSubordinateSims,
        canViewYachtSims: values.canViewYachtSims,
      });
      message.success('User updated successfully');
      setEditModalVisible(false);
      setEditingUser(null);
      editForm.resetFields();
      fetchUsers();
    } catch (err: any) {
      message.error(err.message || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteUser(id);
      message.success('User deactivated successfully');
      fetchUsers();
    } catch (err: any) {
      message.error(err.message || 'Failed to delete user');
    }
  };

  const cardHandlers: UserListCardHandlers = {
    onEdit: handleEdit,
    onDelete: handleDelete,
  };

  const roleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'red';
      case 'reseller_admin':
        return 'orange';
      case 'reseller_support':
        return 'gold';
      case 'customer_admin':
        return 'blue';
      case 'asset_manager':
        return 'green';
      case 'department_head':
        return 'cyan';
      case 'crew':
        return 'default';
      default:
        return 'default';
    }
  };

  // KAN-163: compact layout so the table fits one screen. Email carries the
  // Cognito username underneath, status folds into the Name cell, and the
  // per-row buttons live in a kebab menu (same pattern as OrgsAndAssets).
  const actionItems = (record: User): MenuProps['items'] => {
    // Permanently deleted: Cognito account is gone, row kept for audit history.
    // Nothing can be done with it, so offer nothing.
    if (!record.is_active && !record.cognito_sub) return [];
    if (!record.is_active) {
      return [
        {
          key: 'reactivate',
          label: 'Reactivate',
          icon: <ReloadOutlined />,
          onClick: () => handleReactivate(record),
        },
        { type: 'divider' },
        {
          key: 'purge',
          label: 'Permanently delete',
          icon: <DeleteOutlined />,
          danger: true,
          onClick: () => handlePermanentDelete(record),
        },
      ];
    }
    return [
      { key: 'edit', label: 'Edit', icon: <EditOutlined />, onClick: () => handleEdit(record) },
      ...(record.invite_pending
        ? [
            {
              key: 'resend',
              label: 'Resend invite',
              icon: <MailOutlined />,
              onClick: () =>
                Modal.confirm({
                  title: 'Resend invitation?',
                  content:
                    'Sends a fresh invitation email. The previous temporary password stops working.',
                  okText: 'Resend',
                  onOk: () => handleResendInvite(record),
                }),
            },
          ]
        : []),
      ...(canSendPasswordReset(record)
        ? [
            {
              key: 'password-reset',
              label: 'Send password reset',
              icon: <KeyOutlined />,
              onClick: () =>
                Modal.confirm({
                  title: 'Send password reset?',
                  content: `Emails ${record.email} a link to choose a new password (valid for 24 hours). You will not see or set the password. Their current password keeps working until they use the link.`,
                  okText: 'Send',
                  onOk: () => handlePasswordReset(record),
                }),
            },
          ]
        : []),
      {
        key: 'reset-mfa',
        label: 'Reset MFA',
        icon: <SafetyOutlined />,
        onClick: () =>
          Modal.confirm({
            title: 'Reset MFA?',
            content:
              "Removes the user's authenticator app. They will be asked to set up a new one at next sign-in.",
            okText: 'Reset',
            onOk: () => handleResetMfa(record),
          }),
      },
      { type: 'divider' },
      {
        key: 'deactivate',
        label: 'Deactivate',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () =>
          Modal.confirm({
            title: 'Deactivate user?',
            content:
              "The user will lose access but can be reactivated later. Use 'Show inactive' to find deactivated users.",
            okText: 'Deactivate',
            okButtonProps: { danger: true },
            onOk: () => handleDelete(record.id),
          }),
      },
    ];
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      sorter: (a: User, b: User) =>
        `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(
          `${b.first_name || ''} ${b.last_name || ''}`
        ),
      render: (_: any, record: User) => {
        const name = `${record.first_name || ''} ${record.last_name || ''}`.trim();
        // Users synced from Cognito often have no name yet; fall back to the
        // username so the row is still identifiable.
        return (
          <Link to={`/admin/users/${record.id}`}>
            {name || <Text italic>{record.cognito_username || record.email}</Text>}
          </Link>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      ellipsis: true,
      sorter: (a: User, b: User) => a.email.localeCompare(b.email),
    },
    {
      title: (
        <Space size={4}>
          Username
          <Tooltip title="The Cognito username this person types at sign-in. Email also works.">
            <InfoCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'cognito_username',
      key: 'cognito_username',
      ellipsis: true,
      render: (val: string | null | undefined) => (val ? <Text code>{val}</Text> : '—'),
    },
    {
      title: (
        <Space size={4}>
          Role
          <Tooltip
            title="System-wide permission level. Different from 'Role on Asset' (a per-yacht job label seen on the user profile)."
            placement="top"
          >
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'role',
      key: 'role',
      sorter: (a: User, b: User) => a.role.localeCompare(b.role),
      render: (role: string) => <Tag color={roleColor(role)}>{role}</Tag>,
    },
    {
      title: 'Organization',
      dataIndex: 'org_name',
      key: 'org_name',
      ellipsis: true,
      sorter: (a: User, b: User) => (a.org_name || '').localeCompare(b.org_name || ''),
      render: (name: string) => name || '-',
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_: any, record: User) => {
        if (!record.is_active && !record.cognito_sub)
          return (
            <Tooltip title="Permanently deleted. Kept for audit history only.">
              <Tag>Deleted</Tag>
            </Tooltip>
          );
        if (!record.is_active) return <Tag color="red">Inactive</Tag>;
        if (record.invite_pending)
          return (
            <Tooltip title="Invited but has not activated the account yet (activation link valid 7 days).">
              <Tag color="gold">Invited</Tag>
            </Tooltip>
          );
        return <Tag color="green">Active</Tag>;
      },
    },
    {
      title: 'Last login',
      dataIndex: 'last_login',
      key: 'last_login',
      width: 110,
      sorter: (a: User, b: User) => (a.last_login || '').localeCompare(b.last_login || ''),
      render: (val: string | null) =>
        val ? (
          <Tooltip title={new Date(val).toLocaleString()}>
            {new Date(val).toLocaleDateString()}
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      width: 48,
      render: (_: any, record: User) => {
        const items = actionItems(record);
        if (!items || items.length === 0) return null;
        return (
          <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              aria-label={`Actions for ${record.email}`}
            />
          </Dropdown>
        );
      },
    },
  ];

  const q = search.trim().toLowerCase();
  const filteredUsers = q
    ? users.filter(u =>
        [u.first_name, u.last_name, u.email, u.cognito_username, u.role, u.department, u.org_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q)
      )
    : users;

  return (
    <div className="page-wrapper" style={{ padding: 24 }}>
      <PageHeader
        title="Users"
        actions={
          <>
            <Input.Search
              placeholder="Search name, email, username, role, org"
              allowClear
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 240, marginRight: 8 }}
            />
            <Space style={{ marginRight: 8 }}>
              <Switch checked={showInactive} onChange={setShowInactive} size="small" />
              <Tooltip title="Include users that have been deactivated. Their Cognito accounts are disabled but the platform row is preserved so they can be reactivated.">
                <span style={{ color: '#bbb' }}>Show inactive</span>
              </Tooltip>
            </Space>
            <Button icon={<SyncOutlined />} onClick={handleSync}>
              Sync from Cognito
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              Invite User
            </Button>
            <PageHelp
              title="Users Help"
              sections={[
                {
                  title: 'Global Role (this column)',
                  content:
                    'Each user has exactly one system-wide role that determines what they can see and do:\n\n- Super Admin: full platform access\n- Reseller Admin: manages reseller org and all customers\n- Reseller Support: read-mostly access within their reseller\n- Customer Admin: manages their organization\n- Asset Manager: manages specific assets (yachts) and their crew\n- Department Head: oversees same-department crew on shared assets\n- Crew: sees only their personally assigned SIMs',
                  tips: [
                    'You can only create users with roles below your own',
                    'Moving a user to a different org changes their data access',
                  ],
                },
                {
                  title: '"Role on Asset" is different',
                  content:
                    'A user\'s profile page also shows a "Role on Asset" column under Asset Assignments. That is a per-yacht JOB LABEL (captain, engineer, chef, crew), not a permission. It can differ for each yacht the user is assigned to and has no effect on what data they see.\n\nExample: a super_admin who is "engineer" on Odessa II for support purposes.',
                },
                {
                  title: 'Managing Users',
                  content:
                    'Click a user name to view their full profile with assigned SIM cards and asset memberships.\n\nUse Edit to change name, global role, department, organization, or SIM visibility flags. Use Delete to deactivate (they can no longer log in).',
                },
                {
                  title: 'Sync from Cognito',
                  content:
                    'If users were created directly in AWS Cognito, use "Sync from Cognito" to import them into the system.',
                },
              ]}
            />
          </>
        }
      />

      <ResponsiveTableOrCards
        dataSource={filteredUsers}
        rowKey={record => record.id}
        columns={columns}
        loading={loading}
        breakpoint="xl"
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        renderCard={user => <UserListCard user={user} handlers={cardHandlers} />}
        emptyText="No users found"
      />

      {/* Create Modal */}
      <ResponsiveModal
        title="Invite User"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
      >
        <UserForm
          form={createForm}
          mode="create"
          organizations={organizations}
          onFinish={handleCreate}
        />
        <div style={{ marginTop: 16 }}>
          <Space>
            <Button type="primary" loading={submitting} onClick={() => createForm.submit()}>
              Invite
            </Button>
            <Button
              onClick={() => {
                setCreateModalVisible(false);
                createForm.resetFields();
              }}
            >
              Cancel
            </Button>
          </Space>
        </div>
      </ResponsiveModal>

      {/* Edit Modal */}
      <ResponsiveModal
        title={`Edit User: ${editingUser?.first_name || ''} ${editingUser?.last_name || ''}`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingUser(null);
          editForm.resetFields();
        }}
        footer={null}
      >
        <UserForm
          form={editForm}
          mode="edit"
          organizations={organizations}
          initialValues={editingUser || undefined}
          onFinish={handleUpdate}
        />
        <div style={{ marginTop: 16 }}>
          <Space>
            <Button type="primary" loading={submitting} onClick={() => editForm.submit()}>
              Save
            </Button>
            <Button
              onClick={() => {
                setEditModalVisible(false);
                setEditingUser(null);
                editForm.resetFields();
              }}
            >
              Cancel
            </Button>
          </Space>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default UserList;
